import { IconVideoPlus } from "@tabler/icons-react"
import { type FC } from "react"
import { useDropzone } from "react-dropzone"
import styled from "styled-components"

import PartialLoadingPage from "~/components/PartialLoadingPage"

type Props = { onUpload: (file: File) => Promise<void>; progress: number | null; isDisabled?: boolean }
const VideoUploader: FC<Props> = ({ onUpload, progress, isDisabled = false }) => {
	const dropzone = useDropzone({
		accept: { "video/*": [] },
		disabled: isDisabled,
		async onDrop(acceptedFiles: File[]) {
			if (acceptedFiles.length === 0) return
			await onUpload(acceptedFiles[0])
		},
	})

	return (
		<Dropzone {...dropzone.getRootProps()} $isDisabled={isDisabled}>
			<input {...dropzone.getInputProps()} />
			{getContent({
				isDragActive: dropzone.isDragActive,
				isDragReject: dropzone.isDragReject,
				progress,
			})}
		</Dropzone>
	)
}

const getContent = ({
	isDragActive,
	isDragReject,
	progress,
}: {
	isDragActive: boolean
	isDragReject: boolean
	progress: number | null
}) => {
	if (progress !== null) {
		return (
			<>
				<PartialLoadingPage />
				<ProgressBarWrapper>
					<ProgressBarContainer>
						<ProgressBar style={{ width: `${progress * 100}%` }} />
					</ProgressBarContainer>
					<InfoText>{Math.floor(progress * 100)}%</InfoText>
				</ProgressBarWrapper>
			</>
		)
	}

	if (isDragActive) {
		return <InfoText>{isDragReject ? "That file type is not supported" : `Drop video file here`}</InfoText>
	}

	return (
		<>
			<IconVideoPlus className="text-primary size-16" />
			<InfoText>Click here or drag and drop to upload</InfoText>
		</>
	)
}

const Dropzone = styled.div<{ $isDisabled: boolean }>`
	border: 1px dashed ${props => props.theme.primaryColor};
	border-radius: 8px;
	padding: 32px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: 32px;
	cursor: ${props => (props.$isDisabled ? "default" : "pointer")};
	position: relative;
`

const ProgressBarWrapper = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
`

const ProgressBarContainer = styled.div`
	width: 100%;
	max-width: 600px;
	height: 30px;
	border: 1px solid #cdcdcd;
	border-radius: 8px;
	overflow: hidden;
`

const ProgressBar = styled.div`
	height: 100%;
	background-color: #cdcdcd;
	border-radius: 8px;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	transition: 0.3s ease-in-out;
`

const InfoText = styled.p`
	font-weight: 500;
	font-size: 16px;
	text-align: center;
`

export default VideoUploader
